import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import uuid from "react-uuid";
import { isEmpty } from "lodash-es";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { BrainCog, FolderOpen, Info, Link2, Pencil, PinOff, Presentation, Search, Star } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { createPowerpoint, generateSlideStructure, deleteAborted, generate, getConversation, getCustomGPTObj, getPresentationDownloadUrl, getPresentationStatus, getSasUrl, marketingSearch, notifyUsageChanged, persistRealtimeTurn, removeCustomGPTUser } from "@/api/api";
import { ChatMessage, ChatModel, Conversation, ConversationRequest, ConversationResponseEnvelope, CustomGPT, DEFAULT_PERSONALIZATION, ErrorResponsePayload, IndividualFileChat, PresentationAsset, SlideStructure, StreamErrorDetails } from "@/api/models";
import ConversationFilesPanel from "@/components/ConversationFilesPanel/ConversationFilesPanel";
import { chatModels } from "@/constants/models";
import { applyStreamMessage, buildStreamMessages, createStreamMessageAccumulator, getDialogueMessages, normalizeResponseMessages, preserveCompactionEventsOnError, readNdjsonStream, shouldHideLoadingMessageForChunk, STREAM_MESSAGE_ROLE } from "@/utils/chatStream";
import { changeModel, doTrackEvent, handleCheckPowerpointStatus, handleCheckSlideStructureStatus, handleCheckMarketingStatus, getLogo, newChat, openChat, refreshUserCustomGPTs, setTitle, toggleHistoryPanel, useHandleTransitionEnd } from "@/utils/utils";
import { getEffortLabel } from "@/utils/modelSelection";
import { getErrorDetails, getErrorSummary } from "@/utils/errorMessages";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useRealtimeVoiceSession } from "@/hooks/useRealtimeVoiceSession";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import PromptInput from "@/components/PromptInput/PromptInput";
import LoadingState from "@/components/Answer/LoadingState";
import ContextCompactionDivider from "@/components/Chat/ContextCompactionDivider";
import { textareaRef } from "@/components/PromptInput/promptInputRef";
import VoiceModePanel from "@/components/Voice/VoiceModePanel";
import Message from "@/components/Message/Message";
import Answer from "@/components/Answer/Answer";
import ChatScrollNav from "./ChatScrollNav";
import UploadProgress from "./UploadProgress";
import SearchPopup from "@/components/Search/SearchPopup";
import CustomGPTDetails from "@/components/CustomGPT/CustomGPTDetails";
import RateDialog from "@/components/CustomGPT/RateDialog";
import MessagePopup from "./MessagePopup";
import SlideStructurePreview from "./SlideStructurePreview";
import PresentationAssetCard from "./PresentationAssetCard";
import ConversationShareDialog from "./ConversationShareDialog";
import { downloadUrl, getPresentationFilename } from "../Library/libraryUtils";
import { useUnifiedAuth } from "@/auth/useUnifiedAuth";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import home_logo from "@/assets/home_logo.png";

enum messageStatus {
  NotRunning = "Not Running",
  Processing = "Processing",
  Done = "Done"
}

const welcomeGreetings = [
  "Hi, {name}. What can I help you with today?",
  "Good to see you, {name}. What are we working on?",
  "Ready when you are, {name}. What would you like to explore?",
  "What’s on your mind today, {name}?",
  "Let’s make something useful, {name}. Where should we start?",
  "What would make your day easier, {name}?",
  "Have a question or an idea, {name}? Let’s work through it.",
  "What can I help you figure out today, {name}?",
  "Pick up a new idea, {name}. What are you curious about?",
  "Where can I help you make progress, {name}?",
  "Tell me what you’re thinking about, {name}.",
  "Let’s get started, {name}. What do you need?",
];

const getFirstName = (authName?: string): string | undefined => {
  const trimmedName = authName?.trim();
  if (!trimmedName || trimmedName.includes("@")) return undefined;

  const commaIndex = trimmedName.indexOf(",");
  const givenNames = (commaIndex < 0 ? trimmedName : trimmedName.slice(commaIndex + 1))
    .replace(/\s*\([^()]*\)\s*$/, "")
    .trim();
  if (!givenNames || /[(),@]/.test(givenNames)) return undefined;
  return givenNames.split(/\s+/)[0];
};

const getRandomGreetingIndex = () => Math.floor(Math.random() * welcomeGreetings.length);

const Chat = () => {
  const appStateContext = useContext(AppStateContext);
  const { account } = useUnifiedAuth();
  const { conversationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const appInsightsConnectionString = appStateContext?.state.frontendSettings?.app_insights_connection_string?.trim();
  const ADMINS = appStateContext?.state.frontendSettings?.admins || [];
  useHandleTransitionEnd();
  const handleGetLogo = (brand: string) => getLogo(appStateContext, brand);
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const handleToggleHistoryPanel = () => toggleHistoryPanel(appStateContext);
  const handleModelChange = (model: ChatModel) => changeModel(appStateContext, model);
  const handleOpenChat = (item: Conversation | null, shared: boolean = false, publicCustomGPT: CustomGPT | null = null) => openChat(appStateContext, item, shared, publicCustomGPT);
  const handleRefreshUserCustomGPTs = () => refreshUserCustomGPTs(appStateContext);
  const chatScrollContainer = useRef<HTMLDivElement | null>(null);
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const streamBaseMessagesRef = useRef<ChatMessage[]>([]);
  const streamMessagesRef = useRef<ChatMessage[]>([]);
  const streamAccumulatorRef = useRef(createStreamMessageAccumulator());
  const streamErrorDetailsRef = useRef<StreamErrorDetails | null>(null);
  const pendingInitialScroll = useRef(false);
  const anchoredUserMessageId = useRef<string | null>(null);
  const previousChatId = useRef<string | undefined>(appStateContext?.state.currentChat?.id);
  const previousConversationIdRef = useRef<string>(conversationId || '');
  // Poll chains capture this at start-up and bail once it moves, so a job
  // started in one conversation can never write into the next one.
  const activeChatIdRef = useRef<string | undefined>(appStateContext?.state.currentChat?.id);
  const prevIsLoadingRef = useRef(false);
  const [userMessageScrollSpacerHeight, setUserMessageScrollSpacerHeight] = useState<number>(0);
  const [isLoadingConversation, setIsLoadingConversation] = useState<boolean>(false);
  const [isSummarizing, setIsSummarizing] = useState<boolean>(false);
  const [isSelecting, setIsSelecting] = useState<boolean>(false);
  const [isQuerying, setIsQuerying] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
  const [compactionStatus, setCompactionStatus] = useState<string | null>(null);
  const [, setIsImageGenerating] = useState<boolean>(false);
  const abortFuncs = useRef([] as AbortController[]);
  const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning);
  const [timezone, setTimezone] = useState<string>('');
  const [question, setQuestion] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [presentations, setPresentations] = useState<PresentationAsset[]>([]);
  const [aborted, setAborted] = useState<boolean>(false);
  const [abortedFeedbackMessageIds, setAbortedFeedbackMessageIds] = useState<Set<string>>(() => new Set());
  const [editUserMessage, setEditUserMessage] = useState<string>('');
  const [editUserMessageContent, setEditUserMessageContent] = useState<string>('');
  const [resubmitting, setResubmitting] = useState<boolean>(false);
  const truncationInProgressRef = useRef(false);
  const [migrated, setMigrated] = useState<boolean>(false);
  const [shared, setShared] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [longConversation, setLongConversation] = useState<boolean>(false);
  const [imageSize, setImageSize] = useState<string>('1024x1024');
  const [imageStyle, setImageStyle] = useState<string>('');
  const [marketingSearchLevel, setMarketingSearchLevel] = useState<string>('medium');
  const [isRateDialogOpen, setIsRateDialogOpen] = useState<boolean>(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [isVoicePanelOpen, setIsVoicePanelOpen] = useState<boolean>(false);
  const [copyLinkText, setCopyLinkText] = useState<string>('Copy Link');
  const sendConvStarterRef = useRef(false);

  useEffect(() => {
    setCompactionStatus(null);
  }, [conversationId]);
  
  const [createAiImages, setCreateAiImages] = useState<boolean>(false);
  const [isCreatingPowerpoint, setIsCreatingPowerpoint] = useState<boolean>(false);
  const [isGeneratingSlideStructure, setIsGeneratingSlideStructure] = useState<boolean>(false);
  const [slideStructureData, setSlideStructureData] = useState<SlideStructure | null>(null);
  const [isRegeneratingSlides, setIsRegeneratingSlides] = useState<boolean>(false);
  // Presentations whose durable-status chain is running in this session; polling
  // them here as well would just double the requests.
  const [livePolledPresentationIds, setLivePolledPresentationIds] = useState<string[]>([]);

  useEffect(() => { activeChatIdRef.current = appStateContext?.state.currentChat?.id; });

  useEffect(() => {
    const pendingPresentationIds = presentations
      .filter((asset) => asset.status === "creating" && !livePolledPresentationIds.includes(asset.id))
      .map((asset) => asset.id);
    if (!pendingPresentationIds.length) return;

    const refreshPresentationStatuses = async () => {
      const refreshed = await Promise.all(pendingPresentationIds.map((id) => getPresentationStatus(id)));
      const byId = new Map(refreshed.filter((asset): asset is PresentationAsset => !!asset).map((asset) => [asset.id, asset]));
      if (byId.size) setPresentations((current) => {
        let changed = false;
        const next = current.map((asset) => {
          const refreshedAsset = byId.get(asset.id);
          if (!refreshedAsset || (
            refreshedAsset.status === asset.status
            && refreshedAsset.updatedAt === asset.updatedAt
            && refreshedAsset.error === asset.error
          )) return asset;
          changed = true;
          return {
            ...asset,
            ...refreshedAsset,
            filename: getPresentationFilename(appStateContext?.state.currentChat?.title, refreshedAsset.filename || asset.filename),
          };
        });
        return changed ? next : current;
      });
    };

    void refreshPresentationStatuses();
    const interval = window.setInterval(() => void refreshPresentationStatuses(), 5000);
    return () => window.clearInterval(interval);
  }, [presentations, livePolledPresentationIds]);
  
  const { indexing, uploadProgress, isDragging, processUploads, resolveExistingContext } = useFileUpload();

  const ERROR = "error";
  const currentCustomGPT = appStateContext?.state.currentCustomGPT || null;
  const currentChat = appStateContext?.state.currentChat;
  const isWelcomeScreen = !appStateContext?.state.chatPanelTransitioned
    && isEmpty(currentCustomGPT)
    && isEmpty(currentChat)
    && !conversationId;
  const [welcomeGreetingIndex, setWelcomeGreetingIndex] = useState(getRandomGreetingIndex);
  const previousWelcomeState = useRef({ isWelcomeScreen, locationKey: location.key });
  const firstName = getFirstName(account?.name);
  const welcomeGreeting = firstName
    ? welcomeGreetings[welcomeGreetingIndex].replace("{name}", firstName)
    : "What can I help you with today?";
  const handleNewChat = () => {
    if (isWelcomeScreen) setWelcomeGreetingIndex(getRandomGreetingIndex());
    newChat(appStateContext);
  };

  useEffect(() => {
    const enteredWelcomeScreen = isWelcomeScreen && !previousWelcomeState.current.isWelcomeScreen;
    const navigatedToNewChat = isWelcomeScreen && previousWelcomeState.current.locationKey !== location.key;
    if (enteredWelcomeScreen || navigatedToNewChat) {
      setWelcomeGreetingIndex(getRandomGreetingIndex());
    }
    previousWelcomeState.current = { isWelcomeScreen, locationKey: location.key };
  }, [isWelcomeScreen, location.key]);
  const sharedConversationModelName = currentChat?.model?.name || '';
  const sharedConversationReasoning = currentChat?.reasoning?.trim() || '';
  const sharedConversationReasoningLabel = getEffortLabel(sharedConversationReasoning) || sharedConversationReasoning;
  const sharedConversationModelDetails = sharedConversationReasoningLabel ? `${sharedConversationModelName} · ${sharedConversationReasoningLabel}` : sharedConversationModelName;
  const isImagePromptMode = appStateContext?.state.selectedTool === 'image_generation';
  const dialogueMessages = getDialogueMessages(messages);
  const renderedMessages = dialogueMessages.filter(message => message && message.role !== 'developer');
  const renderedMessageIds = new Set(renderedMessages.map(message => message.id));
  const timelineMessages = messages.filter(message => message && message.role !== 'developer' && (
    message.role !== STREAM_MESSAGE_ROLE.compaction || (!!message.anchorMessageId && renderedMessageIds.has(message.anchorMessageId))
  ));
  const pendingFilesMessageId = indexing ? dialogueMessages[dialogueMessages.length - 1]?.id : undefined;
  const lastMessageId = dialogueMessages[dialogueMessages.length - 1]?.id;
  const presentationIsLast = presentations.some(asset => asset.status !== "failed" && (!asset.parentMessageId || asset.parentMessageId === lastMessageId));
  const voiceModeEnabled = Boolean(appStateContext?.state.frontendSettings?.feature_flags?.voice_mode) && !shared && !readOnly;

  /**
   * Connect application insights
  **/
  useEffect(() => {
    if (appInsightsConnectionString) {
      const appInsights = new ApplicationInsights({ config: {
        connectionString: appInsightsConnectionString
      } });
      appInsights.loadAppInsights();
      appInsights.trackPageView();
    }
  }, [appInsightsConnectionString]);

  /**
   * Initial page load
  **/
  useEffect(() => {
    // Get user's timezone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(userTimeZone);

    // Auto-focus on text box
    textareaRef.current?.focus();
  }, []);

  /** 
   * Handle loading shared chat from passed conversation ID
  **/
  useEffect(() => {
    // Load user's conversation
    if (appStateContext?.state.customGPTsLoaded && location.pathname.substring(0, 3) == '/c/' && conversationId && !isEmpty(conversationId) && appStateContext?.state.currentChat?.id != conversationId) loadChat(conversationId, true, false);
    // Load shared conversation
    else if (appStateContext?.state.customGPTsLoaded && location.pathname.substring(0, 7) == '/share/' && conversationId && !isEmpty(conversationId)) loadChat(conversationId, true, true);
  }, [appStateContext?.state.customGPTsLoaded, appStateContext?.state.customGPTs, conversationId]);

  /** 
   * Handle automatically sending a conversation starter if one is selected
  **/
  useEffect(() => {
    if (!isEmpty(appStateContext?.state.selectedConvStarter) && !sendConvStarterRef.current) {
      sendConvStarterRef.current = true;
      const convStarter = appStateContext?.state.selectedConvStarter;
      appStateContext?.dispatch({ type: 'SET_SELECTED_CONVERSATION_STARTER', payload: null });
      if (convStarter) sendPrompt(convStarter);
    }
  }, [appStateContext?.state.selectedConvStarter]);

  /**
   * Close slide structure preview when navigating to a different conversation
  **/
  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    const nextConversationId = conversationId || '';
    previousConversationIdRef.current = nextConversationId;

    setSlideStructureData(null);
    setIsGeneratingSlideStructure(false);
    setIsRegeneratingSlides(false);
    setIsCreatingPowerpoint(false);
    setPresentations([]);
    setLivePolledPresentationIds([]);
    setIsLoading(false);
    setShowLoadingMessage(false);
    setProcessMessages(messageStatus.NotRunning);

    const voiceSessionActive = voiceSession.state.status === 'connecting' || voiceSession.state.status === 'listening' || voiceSession.state.status === 'speaking';
    const keepVoicePanelOpen = isVoicePanelOpen && voiceSessionActive && !previousConversationId && !!nextConversationId;
    if (!keepVoicePanelOpen) {
      setIsVoicePanelOpen(false);
      void voiceSession.stopSession('idle');
    }
  }, [conversationId]);

  /**
  * Handle clearing state when conversation is cleared
  **/
  useEffect(() => {
    if (isEmpty(appStateContext?.state.currentChat) && !appStateContext?.state.chatPanelTransitioned) {
      setTitle(appStateContext, '');
      setMessages([]);
      streamBaseMessagesRef.current = [];
      streamMessagesRef.current = [];
      streamAccumulatorRef.current = createStreamMessageAccumulator();
      setUserMessageScrollSpacerHeight(0);
      setMigrated(false);
      if (isEmpty(conversationId)) setShared(false);
      setReadOnly(false);
      setShared(false);
      setEditUserMessage('');
      pendingInitialScroll.current = false;
      anchoredUserMessageId.current = null;
      setSlideStructureData(null);
      setIsGeneratingSlideStructure(false);
      setIsRegeneratingSlides(false);
      setIsCreatingPowerpoint(false);
    }
  }, [appStateContext?.state.currentChat, appStateContext?.state.chatPanelTransitioned, conversationId]);

  // RAF-based smooth scroll — works regardless of the container's overflow CSS
  const smoothScrollTo = (el: HTMLElement, target: number, duration = 300) => {
    const start = el.scrollTop;
    const diff = target - start;
    if (diff === 0) return;
    const startTime = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      el.scrollTop = start + diff * eased;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  /** 
   * Handle counting user messages in current conversation
  **/
  useEffect(() => {
    // Diagnostic: a falsy element in `messages` crashes every render that reads
    // `.role`. We've guarded the render, but log the payload so the source of
    // the bad element can be identified. Safe to remove once root cause is found.
    if (messages.some(m => !m)) console.warn('[Chat] messages contains a falsy element:', messages);
    const userMessages = messages.filter(message => message && message.role === 'user');
    if (userMessages.length >= 25) setLongConversation(true);
    else setLongConversation(false);
  }, [messages]);

  /**
   * New message mode — initial scroll when a prompt is sent.
   * Fires once per send (pendingInitialScroll guards re-firing during streaming).
   *
   * - Prompt > 75% viewport height → scroll so the bottom of the prompt is at the
   *   top of the viewport (response immediately visible)
   * - Prompt ≤ 75% viewport height → scroll so the top of the prompt sits 16px from
   *   the top of the viewport
   *
   * Grows a bottom spacer until targetScrollTop is reachable, then scrolls once.
  **/
  useLayoutEffect(() => {
    if (!pendingInitialScroll.current || !anchoredUserMessageId.current) return;

    const scrollContainer = chatScrollContainer.current;
    if (!scrollContainer) return;

    const msgEl = scrollContainer.querySelector(
      `[data-user-message-id="${anchoredUserMessageId.current}"]`
    ) as HTMLElement | null;
    if (!msgEl) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const msgRect = msgEl.getBoundingClientRect();
    const containerHeight = scrollContainer.clientHeight;
    const SCROLL_PADDING = 16;

    // Absolute position of message top/bottom within scroll container's content
    const msgTopAbs = scrollContainer.scrollTop + (msgRect.top - containerRect.top);
    const msgBottomAbs = scrollContainer.scrollTop + (msgRect.bottom - containerRect.top);

    const targetScrollTop = msgRect.height > containerHeight * 0.75
      ? Math.max(0, msgBottomAbs)        // response starts visible at top of viewport
      : Math.max(0, msgTopAbs - SCROLL_PADDING); // message top near top of viewport

    const naturalMax = scrollContainer.scrollHeight - userMessageScrollSpacerHeight - containerHeight;
    const requiredSpacer = Math.max(0, targetScrollTop - naturalMax);

    if (Math.abs(requiredSpacer - userMessageScrollSpacerHeight) > 1) {
      setUserMessageScrollSpacerHeight(requiredSpacer);
    } else {
      smoothScrollTo(scrollContainer, targetScrollTop);
      textareaRef.current?.focus();
      pendingInitialScroll.current = false;
    }
  }, [messages, userMessageScrollSpacerHeight]);

  /**
   * New message mode — trim spacer after generation ends without moving the view.
   *
   * The spacer only needs to be large enough to keep scrollTop valid (i.e. prevent
   * the browser from clamping it). neededSpacer is the exact minimum for that.
   * Because we never set spacer BELOW neededSpacer, the browser won't clamp scrollTop
   * and the view stays exactly where it is — no compensation needed.
  **/
  useLayoutEffect(() => {
    if (!prevIsLoadingRef.current || isLoading) {
      prevIsLoadingRef.current = isLoading;
      return;
    }
    prevIsLoadingRef.current = false;

    anchoredUserMessageId.current = null;
    pendingInitialScroll.current = false;

    if (userMessageScrollSpacerHeight === 0) return;

    const scrollContainer = chatScrollContainer.current;
    if (!scrollContainer) return;

    const naturalScrollHeight = scrollContainer.scrollHeight - userMessageScrollSpacerHeight;
    const naturalMax = naturalScrollHeight - scrollContainer.clientHeight;
    const neededSpacer = Math.max(0, scrollContainer.scrollTop - naturalMax);

    if (neededSpacer < userMessageScrollSpacerHeight - 1) {
      setUserMessageScrollSpacerHeight(neededSpacer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  /**
   * Reset scroll state when the active chat changes.
  **/
  useEffect(() => {
    const currentChatId = appStateContext?.state.currentChat?.id;

    if (!previousChatId.current && currentChatId) {
      previousChatId.current = currentChatId;
      return;
    }

    if (previousChatId.current && currentChatId && previousChatId.current !== currentChatId) {
      if (userMessageScrollSpacerHeight !== 0) setUserMessageScrollSpacerHeight(0);
      pendingInitialScroll.current = false;
      anchoredUserMessageId.current = null;
    }

    previousChatId.current = currentChatId;
  }, [appStateContext?.state.currentChat?.id, userMessageScrollSpacerHeight]);

  /**
   * Handle loading conversation
   * 
   * @param {string} convId - Conversation ID
   * @param {boolean} load - Whether to load the conversation or not
   * @param {boolean} shared - Whether the conversation is shared or not
  **/
  const loadChat = async (convId: string, load: boolean = false, shared: boolean = false) => {
    // If conversation needs loaded
    if (load) {
      // Saved conversations must not inherit the spacer used to anchor a new prompt.
      setUserMessageScrollSpacerHeight(0);
      pendingInitialScroll.current = false;
      anchoredUserMessageId.current = null;
      setIsLoadingConversation(true);
      appStateContext?.dispatch({ type: 'SET_SELECTED_CITATIONS', payload: null });
      appStateContext?.dispatch({ type: 'SET_CHATPANEL_TRANSITIONED', payload: true });

      if (screen.width <= 700) {
        localStorage.setItem('sms-gpt_sidepanelOpen', '0');
        appStateContext?.dispatch({ type: 'SET_SIDEPANEL', payload: false });
      }
      const conversation = await getConversation(convId, shared);

      // If conversation found
      if (conversation) {
        // If conversation is with a Custom GPT, check if it's public
        let publicCustomGPT = null; 
        if (!isEmpty(conversation.customGPTID)) {
          const customGPT = await getCustomGPTObj(conversation.customGPTID);
          if (customGPT && customGPT.visibility == '0') publicCustomGPT = customGPT;
        }

        // User's conversation or shared conversation that user can access
        if (!shared || (shared && (isEmpty(conversation.customGPTID) || publicCustomGPT || appStateContext?.state.customGPTs?.find(c => c.id === conversation.customGPTID) || ADMINS.includes(appStateContext?.state.currentUser?.id || '')))) {
          handleOpenChat(conversation, shared, publicCustomGPT);

          // If conversation is with uploaded files
          if (conversation.files) {
            const individualFileChat: IndividualFileChat[] = []
            // filter/dedupe: legacy records can carry an empty leading entry (the old
            // merge produced "|a.pdf" when files was "") or repeated names.
            const filenames = Array.from(new Set(conversation.files.split('|').filter(Boolean)));
            filenames.forEach((file) => {
              individualFileChat.push({
                id: conversation.id || '',
                filename: file,
                imageEditing: false
              });
            });
            appStateContext?.dispatch({ type: 'SET_INDIVIDUAL_FILE_CHAT', payload: individualFileChat });
          }
          // If conversation is with a single file (image)
          else if (conversation.filename) {
            const individualFileChat: IndividualFileChat[] = [{
              id: conversation.id || '',
              filename: conversation.filename,
              imageEditing: false
            }];
            appStateContext?.dispatch({ type: 'SET_INDIVIDUAL_FILE_CHAT', payload: individualFileChat });
          }
          // If no files
          else appStateContext?.dispatch({ type: 'SET_INDIVIDUAL_FILE_CHAT', payload: null });

          setTitle(appStateContext, conversation.title);
          setMessages(conversation.messages);
          setPresentations(conversation.presentations || []);
          setMigrated(conversation.migrated || false);
          if (isEmpty(convId)) setShared(false);
          setReadOnly(conversation.migrated || shared || (!isEmpty(conversation.customGPTID) && !publicCustomGPT && !appStateContext?.state.customGPTs?.find(c => c.id == conversation?.customGPTID)));
          setShared(shared);
          if (shared) handleTrackEvent('view', 'conv_view', 'Conversation - View Shared', conversation?.id || '', '', '');
          setEditUserMessage('');
          // Jump straight to the latest message — no animation when opening a chat
          setTimeout(() => {
            const scrollContainer = chatScrollContainer.current;
            if (scrollContainer) scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }, 100);
          textareaRef.current?.focus();
        }
        // Otherwise, if shared conversation with Custom GPT that user doesn't have access to, redirect them to the Custom GPT
        else if (!isEmpty(conversation.customGPTID) && !appStateContext?.state.customGPTs?.find(c => c.id === conversation.customGPTID)) navigate('/spaces/v/' + conversation.customGPTID);
      }
      // Otherwise, navigate home
      else {
        appStateContext?.dispatch({ type: 'SET_CHATPANEL_TRANSITIONED', payload: false });
        navigate('/');
      }

      setIsLoadingConversation(false);
    }
    // Otherwise, conversation was just created, and URL just needs updated properly
    else navigate('/c/' + convId);
  }

  /** 
   * Handle processing model responses into correct formats
   * 
   * @param {ChatMessage} resultMessage - The model's response
   * @param {ChatMessage} userMessage - The user's prompt
   * @param {string} [conversationId] - ID of the current conversation to attach the messages to
  **/
  const startPathRef = useRef<string>(location.pathname);

  const isOnGenerationTarget = () => {
    return location.pathname === startPathRef.current;
  };

  const processResultMessage = (resultMessage: ChatMessage) => {
    applyStreamMessage(streamAccumulatorRef.current, resultMessage, {
      setIsSelecting,
      setIsSummarizing,
      setIsQuerying,
      setIsImageGenerating,
      setShowLoadingMessage,
      setCompactionStatus: (status) => {
        if (isOnGenerationTarget()) setCompactionStatus(status);
      },
    });

    const newMessages = buildStreamMessages(
      streamBaseMessagesRef.current,
      streamAccumulatorRef.current,
    );

    streamMessagesRef.current = newMessages;
    if (isOnGenerationTarget()) setMessages(newMessages);
  }

  const buildErrorChatMessage = (
    message: string,
    errorPayload?: Partial<ErrorResponsePayload>,
    details?: StreamErrorDetails | null,
  ): ChatMessage => {
    const normalizedDetails = getErrorDetails(message, details || errorPayload?.error_details);
    const errorDetails = errorPayload?.error_code && !normalizedDetails.error_code
      ? { ...normalizedDetails, error_code: errorPayload.error_code }
      : normalizedDetails;
    return {
      id: uuid(),
      role: ERROR,
      content: getErrorSummary(message, errorDetails),
      date: new Date().toISOString(),
      error_code: errorPayload?.error_code,
      error_type: errorPayload?.error_type,
      error_details: errorDetails,
    };
  };

  const removeAbortController = (abortController: AbortController) => {
    abortFuncs.current = abortFuncs.current.filter((controller) => controller !== abortController);
  }

  const syncBaseMessages = (baseMessages: ChatMessage[]) => {
    streamBaseMessagesRef.current = baseMessages;
    streamMessagesRef.current = baseMessages;
    setMessages(baseMessages);
  }

  const getFinalizedMessages = () => {
    if (streamMessagesRef.current.length > 0) {
      return [...streamMessagesRef.current];
    }

    return buildStreamMessages(
      streamBaseMessagesRef.current,
      streamAccumulatorRef.current,
    );
  }

  const syncConversationState = (conversation: Conversation, finalizedMessages: ChatMessage[]) => {
    const state = appStateContext?.state;
    if (!state) return conversation;

    const finalizedConversation = { ...conversation, messages: finalizedMessages };
    const isNewConversation = !state.chatHistory?.some(c => c.id === finalizedConversation.id);
    const isOnThisConversation = location.pathname === '/c/' + finalizedConversation.id || location.pathname === '/share/' + finalizedConversation.id;
    const isOnHomePage = location.pathname === '/';

    // Update the active chat view if the user is still on this conversation,
    // or if they are on the home page and just created a new conversation.
    if (isOnThisConversation || (isOnHomePage && isNewConversation)) {
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: finalizedConversation });

      // Navigate to the conversation URL if not already there (needed for new conversations).
      if (
        location.pathname !== '/c/' + finalizedConversation.id &&
        location.pathname !== '/share/' + finalizedConversation.id
      ) {
        // The turn's generation target moves with it: `location` is the live
        // window.location, so without this every isOnGenerationTarget() check after
        // the navigate fails and the finalized messages (e.g. a stream error) are
        // never drawn over the streamed ones.
        if (startPathRef.current === location.pathname) startPathRef.current = '/c/' + finalizedConversation.id;
        navigate('/c/' + finalizedConversation.id);
      }
    }

    // Always update sidebar so new or updated conversations appear there.
    appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: finalizedConversation });

    return finalizedConversation;
  };
  
  // syncConversationState only updates app state (current chat + sidebar). The rendered
  // transcript is driven by local `messages`, and nothing mirrors app state back into it,
  // so any path that ends the turn via syncConversationState must also push here — or the
  // message is stored but never drawn.
  const renderFinalizedMessages = (finalizedMessages: ChatMessage[]) => {
    if (!isOnGenerationTarget()) return;
    streamMessagesRef.current = finalizedMessages;
    setMessages(finalizedMessages);
  };

  const handleRealtimeTurnComplete = async ({ userTranscript, assistantTranscript, assistantContext, model }: { userTranscript: string; assistantTranscript: string; assistantContext: { citations?: any[]; } | null; model: string; }) => {
    const resolvedModel = appStateContext?.state.currentModel || (chatModels.find(m => m.default) || chatModels[0]);
    const persisted = await persistRealtimeTurn({
      conversation_id: appStateContext?.state.currentChat?.id || '',
      user_transcript: userTranscript,
      assistant_transcript: assistantTranscript,
      currentModel: resolvedModel,
      currentCustomGPT: appStateContext?.state.currentCustomGPT || null,
      currentAgent: appStateContext?.state.currentAgent || null,
      personalization: appStateContext?.state.personalization || DEFAULT_PERSONALIZATION,
      selected_tool: appStateContext?.state.selectedTool || '',
      assistant_context: assistantContext,
      model,
      timezone,
    });

    if (!persisted) throw new Error('Failed to save the voice turn.');

    if (!appStateContext?.state.chatPanelTransitioned) {
      appStateContext?.dispatch({ type: 'SET_CHATPANEL_TRANSITIONED', payload: true });
    }

    setTitle(appStateContext, persisted.conversation.title);
    streamBaseMessagesRef.current = persisted.messages;
    streamMessagesRef.current = persisted.messages;
    setMessages(persisted.messages);
    syncConversationState(persisted.conversation, persisted.messages);
  };

  const voiceSession = useRealtimeVoiceSession({
    enabled: voiceModeEnabled,
    conversationId: appStateContext?.state.currentChat?.id || conversationId,
    currentModel: appStateContext?.state.currentModel || (chatModels.find(m => m.default) || chatModels[0]),
    currentCustomGPT: appStateContext?.state.currentCustomGPT || null,
    currentAgent: appStateContext?.state.currentAgent || null,
    selectedTool: appStateContext?.state.selectedTool || null,
    messages,
    personalization: appStateContext?.state.personalization || DEFAULT_PERSONALIZATION,
    timezone,
    onTurnComplete: handleRealtimeTurnComplete,
  });

  /**
   * Handle sending prompt to backend
   * 
   * @param {string} question - The user's prompt
   * @param {string} [conversationId] - ID of the current conversation to attach the messages to
  **/
  const sendPrompt = async (question: string, conversationId?: string, continuation: boolean = false, retainedMessages?: ChatMessage[]) => {
    if (truncationInProgressRef.current) return;
    // If not transitined yet (first message)
    if (!appStateContext?.state.chatPanelTransitioned) appStateContext?.dispatch({ type: 'SET_CHATPANEL_TRANSITIONED', payload: true });

    // Remove any warnings currently showing
    const warnings = document.getElementsByClassName('truncation-warning');
    Array.from(warnings).forEach(warning => warning.remove());

    const baseMessages = retainedMessages ?? messages;
    const baseDialogue = getDialogueMessages(baseMessages);
    const latestDialogueMessage = baseDialogue[baseDialogue.length - 1];
    const activeMessages =
      aborted && latestDialogueMessage?.role === 'user'
        ? baseMessages.filter(message => message.id !== latestDialogueMessage.id && !(message.role === STREAM_MESSAGE_ROLE.compaction && message.anchorMessageId === latestDialogueMessage.id))
        : baseMessages;

    if (activeMessages !== messages) {
      setMessages(activeMessages);
    }

    setIsLoading(true);
    if (appStateContext?.state.selectedTool == 'ms_office_data') setIsQuerying(true);
    setShowLoadingMessage(true);
    const abortController = new AbortController();
    abortFuncs.current.unshift(abortController);
    setAborted(false);
    setEditUserMessage('');
    streamAccumulatorRef.current = createStreamMessageAccumulator();
    streamErrorDetailsRef.current = null;

    // If 'Custom GPT Selector' tool is selected, set selecting state
    if (appStateContext?.state.selectedTool == 'cgpt_selector') setIsSelecting(true);

    const userMessage: ChatMessage = {
      id: uuid(),
      role: "user",
      content: question,
      context: '',
      date: new Date().toISOString(),
    };

    // Draw the user's message before the upload starts. Uploading and indexing can
    // run for minutes, and SET_CHATPANEL_TRANSITIONED above has already removed the
    // empty-state hero — so without this the transcript renders an empty list for the
    // entire wait. Attached filenames are known client-side, so the chips can show
    // straight away rather than appearing only once indexing resolves.
    const pendingFileList = appStateContext?.state.fileUpload?.current?.files;
    if (pendingFileList?.length) {
      userMessage.files = Array.from<File>(pendingFileList).map((f) => f.name).join('|');
    }
    pendingInitialScroll.current = true;
    anchoredUserMessageId.current = userMessage.id;
    setMessages([...activeMessages, userMessage]);

    const uploadResult = await processUploads(
      conversationId || '',
      userMessage,
      appStateContext?.state.currentChat?.messages.filter(message => message.role !== STREAM_MESSAGE_ROLE.compaction).length || 0
    );

    if (uploadResult.error) {
      const errorChatMsg = buildErrorChatMessage(
        uploadResult.errorMessage || `There was an error uploading the file, please refresh the page and try again. If the problem persists, please put in a ticket.`,
        undefined,
        uploadResult.errorDetails,
      );
      // Keep the prompt visible beside the error — the message is already on screen
      // and the user should not lose it because the upload failed. Nothing indexed,
      // so drop the optimistic file chips.
      userMessage.files = '';
      setMessages([...activeMessages, userMessage, errorChatMsg]);
      setIsLoading(false);
      setShowLoadingMessage(false);
      removeAbortController(abortController);
      return;
    }

    const { initialFile, addedFile, directFiles, effectiveModel } = uploadResult;
    userMessage.model = effectiveModel.deployment;
    let { filesStr, filename, imageBlobURL } = uploadResult;
    conversationId = uploadResult.conversationId || conversationId;
    startPathRef.current = location.pathname;

    const existing = resolveExistingContext(initialFile, addedFile);
    if (!filesStr) filesStr = existing.filesStr;
    if (!filename) filename = existing.filename;

    // If Marketing Graph RAG search level is set to 'high', first send prompt and poll for completion
    let marketingGuid = '';
    if (!isEmpty(appStateContext?.state.currentCustomGPT) && appStateContext?.state.currentCustomGPT.id == 'e7e6d46d-dd49-4a08-b02a-4499de9d540a' && marketingSearchLevel == 'high') {
      // Send prompt to backend to initiate search
      const search_resp = await marketingSearch(question);

      // If initiated successfully
      if (search_resp && search_resp.status === 200) {
        // Decode response
        const data = await search_resp.json();

        // Set marketing GUID to track status
        marketingGuid = data['res']['guid'] || '';

        // Start polling for results
        if (!isEmpty(data['res']['guid'])) {
          // Define tracking variables and start polling loop
          let timeElapsed = 0;
          let queryProcessed;
          let queryProcessingComplete = false;
          while (!queryProcessingComplete) {
            // Make API call to check status
            queryProcessed = await handleCheckMarketingStatus(data['res']['guid']);
            
            // Parse results into respective variables
            queryProcessingComplete = queryProcessed;

            // If processing has not yet completed, wait 15 seconds and try again
            if (!queryProcessingComplete) {
              console.log('Condition not met. ' + (timeElapsed / 1000) + ' seconds elapsed. Retrying in 15 seconds...');
              await new Promise((resolve) => setTimeout(resolve, 15000));
              timeElapsed += 15000;
            }
          }
        }
      }
    }

    // Build parameters to send in prompt generation API call
    let existingConversation: Conversation | null = null;

    let requestMessages: ChatMessage[] = [];
    let displayMessages: ChatMessage[] = [];
    if (conversationId && !initialFile) {
      existingConversation = appStateContext?.state?.currentChat || null;
      if (!existingConversation) {
        console.error("Conversation not found.");
        setIsLoading(false);
        setShowLoadingMessage(false);
        removeAbortController(abortController);
        return;
      }

      const existingMessages = (retainedMessages ?? existingConversation.messages).filter((answer) => answer.role !== ERROR);
      displayMessages = [...existingMessages, userMessage];
      requestMessages = [
        ...getDialogueMessages(existingMessages),
        userMessage,
      ];
    } else {
      requestMessages = [userMessage];
      displayMessages = requestMessages;
    }

    const request: ConversationRequest = { messages: requestMessages };
    syncBaseMessages(displayMessages);
    
    // Send prompt generatation request
    let result = {} as ConversationResponseEnvelope;
    let discardPartialOnError = false;
    let errorResponseMessage = "Please try again. If the problem persists, please put in a ticket.";
    try {
      const response = await generate(
        request, effectiveModel, appStateContext?.state.currentCustomGPT || null, appStateContext?.state.currentAgent || null,
        abortController.signal, 
        initialFile, addedFile, directFiles, conversationId, filesStr, filename, imageBlobURL, 
        appStateContext?.state.selectedTool || '', imageSize, imageStyle, 
        marketingSearchLevel, marketingGuid,
        continuation, timezone, appStateContext?.state.personalization || DEFAULT_PERSONALIZATION
      );
      if (!response?.ok) {
        const responseJson = await response.json() as ErrorResponsePayload;
        errorResponseMessage = responseJson.error === undefined ? errorResponseMessage : responseJson.error;
        // Backend copy is already a complete, curated sentence; only the local
        // fallback ("Please try again...") needs the lead-in.
        const errorChatMsg = buildErrorChatMessage(
          responseJson.error !== undefined
            ? errorResponseMessage
            : `There was an error generating a response. ${errorResponseMessage}`,
          responseJson,
          responseJson.error_details,
        );

        if (existingConversation) {
          const resultConversation = {
            ...existingConversation,
            model: effectiveModel,
            messages: [...displayMessages, errorChatMsg]
          };
          syncConversationState(resultConversation, resultConversation.messages);
          renderFinalizedMessages(resultConversation.messages);
        } else {
          setMessages([...displayMessages, errorChatMsg]);
        }

        return;
      }

      if (response?.body) {
        let streamDone = false;
        let streamPersistenceConfirmed = false;
        // The terminal-marker contract belongs to the NDJSON stream. Some turns
        // answer with a single jsonify'd envelope instead (e.g. the MS Office tool
        // asking for clarification), which legitimately carries no marker.
        const isNdjsonStream = (response.headers.get("content-type") || "").includes("json-lines");
        await readNdjsonStream(response.body, async (parsedResult) => {
          if ((parsedResult as { discard_partial_on_error?: boolean }).discard_partial_on_error === true) {
            discardPartialOnError = true;
            return;
          }

          // Provider output is complete. Keep the turn loading until the
          // persistence acknowledgement below arrives so the new answer cannot
          // receive feedback before its message exists in Cosmos.
          if ((parsedResult as any).__stream_done__) {
            streamDone = true;
            if (isOnGenerationTarget()) {
              setCompactionStatus(null);
              setProcessMessages(messageStatus.Done);
              setShowLoadingMessage(false);
            }
            return;
          }

          const persistenceResult = (parsedResult as any).__messages_persisted__;
          if (persistenceResult !== undefined) {
            if (persistenceResult?.ok !== true) {
              throw new Error("The response could not be saved. Please try again.");
            }
            streamPersistenceConfirmed = true;
            if (isOnGenerationTarget()) setIsLoading(false);
            return;
          }

          if ((parsedResult as ChatMessage).role === "status") {
            processResultMessage(parsedResult as ChatMessage);
            return;
          }

          // Backend hit an unrecoverable error after the HTTP response already
          // started streaming (e.g. upstream model overloaded mid-stream). The
          // 200 is already committed, so the only signal is this marker — throw
          // so the outer catch renders the standard error message instead of
          // silently stopping the spinner with no response.
          if ((parsedResult as any).__stream_error__) {
            if (isOnGenerationTarget()) setCompactionStatus(null);
            const errorChunk = parsedResult as { error?: unknown; error_details?: StreamErrorDetails };
            const backendMsg = errorChunk.error;
            // Stash diagnostics before unwinding — `throw` only carries a string,
            // and the catch below is what actually builds the error message.
            streamErrorDetailsRef.current = errorChunk.error_details || null;
            throw new Error(
              typeof backendMsg === "string" && backendMsg.trim()
                ? backendMsg
                : `There was an error generating a response. ${errorResponseMessage}`
            );
          }

          // Don't revive the generating animation for trailing chunks that may
          // arrive after the done marker (e.g. web-search citations emitted with
          // the final response.completed event).
          if (!streamDone && isOnGenerationTarget()) setProcessMessages(messageStatus.Processing);

          const nextResult = parsedResult as ConversationResponseEnvelope;
          // Trailing tool events may omit turn metadata; keep it for new-chat
          // finalization, sidebar updates, navigation, and the error path.
          result = {
            ...nextResult,
            history_metadata: nextResult.history_metadata ?? result.history_metadata,
          };

          if (result.error) {
            throw new Error(typeof result.error === "string" ? result.error : result.error?.message);
          }

          if (result.choices?.length > 0 || (result as any).role === STREAM_MESSAGE_ROLE.compaction) {
            const normalizedMessages = normalizeResponseMessages(result);
            if (isOnGenerationTarget() && shouldHideLoadingMessageForChunk(normalizedMessages)) setShowLoadingMessage(false);
            normalizedMessages.forEach((resultObj) => {
              processResultMessage(resultObj);
            });
            return;
          }

          // IMAGES
          if (result.url) {
            processResultMessage({
              id: result.id || uuid(),
              date: new Date().toISOString(),
              role: 'image',
              content: result.url || ''
            });
            return;
          }

        // An NDJSON turn always closes with a terminal marker, so one that ends
        // without it was cut short (dropped connection) and must surface as a
        // retryable error rather than a half-written turn.
        }, { requireTerminalMarker: isNdjsonStream });

        if (isNdjsonStream && !streamPersistenceConfirmed) {
          throw new Error("The response could not be confirmed as saved. Please retry.");
        }

        if (!abortController.signal.aborted) {
          const finalizedMessages = getFinalizedMessages();
          let resultConversation: Conversation | null = null;

          if (existingConversation) {
            resultConversation = {
              ...existingConversation,
              model: effectiveModel,
              files: !isEmpty(filesStr)
                ? Array.from(new Set([...(existingConversation.files?.split('|') || []), ...filesStr.split('|')].filter(Boolean))).join('|')
                : existingConversation.files,
              messages: finalizedMessages,
            };
          } else if (result.history_metadata) {
            resultConversation = {
              id: result.history_metadata.conversation_id,
              title: result.history_metadata.title,
              model: effectiveModel,
              customGPTID: appStateContext?.state.currentCustomGPT?.id || '',
              selected_tool: appStateContext?.state.selectedTool || '',
              messages: finalizedMessages,
              date: result.history_metadata.date,
              files: filesStr,
              filename: filename,
              direct_files: directFiles,
              migrated: false,
              shared: 0
            };
          }

          if (!resultConversation) {
            console.error("Conversation not found.");
            return;
          }

          syncConversationState(resultConversation, finalizedMessages);
          renderFinalizedMessages(resultConversation.messages);

          if (isOnGenerationTarget()) {
            if (streamMessagesRef.current !== finalizedMessages) {
              streamMessagesRef.current = finalizedMessages;
              setMessages(finalizedMessages);
            }

            setProcessMessages(messageStatus.Done);
          }
        }
      }
    } catch (e)  {
      if (!abortController.signal.aborted) {
        let errorMessage = `An error occurred. ${errorResponseMessage}`;
        if (result.error?.message) errorMessage = result.error.message;
        else if (typeof result.error === "string") errorMessage = result.error;
        else if (e instanceof Error && !isEmpty(e.message)) errorMessage = e.message;

        const errorChatMsg = buildErrorChatMessage(errorMessage, undefined, streamErrorDetailsRef.current);

        // Preserve ordinary partial answers, but roll back provisional tabular
        // answers because the backend discards them when the handoff fails.
        const streamedMessages = getFinalizedMessages();
        // Only this turn's output counts — the finalized list is prefixed with the
        // request messages, so earlier answers would otherwise read as a partial.
        // Content-filtered output is also discarded by the backend.
        const hasPartialAnswer = !discardPartialOnError && (streamErrorDetailsRef.current as StreamErrorDetails | null)?.reason !== 'content_filter' && streamedMessages
          .slice(displayMessages.length)
          .some((msg) => msg.role === 'assistant' && typeof msg.content === 'string' && !isEmpty(msg.content.trim()));
        const messagesWithError = hasPartialAnswer
          ? [...streamedMessages, errorChatMsg]
           : [...preserveCompactionEventsOnError(displayMessages, streamedMessages), errorChatMsg];

        if (existingConversation) {
          const resultConversation = {
            ...existingConversation,
            model: effectiveModel,
            messages: messagesWithError,
            files: !isEmpty(filesStr)
              ? Array.from(new Set([...(existingConversation.files?.split('|') || []), ...filesStr.split('|')].filter(Boolean))).join('|')
              : existingConversation.files,
          };
          syncConversationState(resultConversation, resultConversation.messages);
          renderFinalizedMessages(resultConversation.messages);
        } else if (result.history_metadata) {
          const resultConversation: Conversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            model: effectiveModel,
            customGPTID: appStateContext?.state.currentCustomGPT?.id || '',
            selected_tool: appStateContext?.state.selectedTool || '',
            messages: messagesWithError,
            date: result.history_metadata.date,
            files: filesStr,
            filename: filename,
            direct_files: directFiles,
            migrated: false,
            shared: 0
          };
          syncConversationState(resultConversation, resultConversation.messages);
          renderFinalizedMessages(resultConversation.messages);
        } else if (isOnGenerationTarget()) {
          setMessages(messagesWithError);
        }
      } else if (isOnGenerationTarget()) {
        setMessages(displayMessages);
      }
    } finally {
      setIsLoading(false);
      setIsImageGenerating(false);
      setShowLoadingMessage(false);
      setCompactionStatus(null);
      setProcessMessages(messageStatus.NotRunning);
      removeAbortController(abortController);
      startPathRef.current = '';
      notifyUsageChanged();
    }

    return abortController.abort();
  }

  /**
   * Handle aborting generation and deleting any messages that were generated and saved
  **/
  const truncateConversationFrom = async (messageId: string) => {
    const id = appStateContext?.state.currentChat?.id;
    if (!id) return true;
    const response = await deleteAborted(id, messageId);
    if (response.ok) return true;
    setMessages(current => [...current.filter(message => message.role !== ERROR), buildErrorChatMessage(
      "Unable to update conversation history. Please try again before sending another message.",
    )]);
    return false;
  };

  const stopGenerating = async () => {
    if (truncationInProgressRef.current) return;
    const partialFeedbackMessageIds = getFinalizedMessages()
      .slice(streamBaseMessagesRef.current.length)
      .filter((message) => (message.role === 'assistant' || message.role === 'image') && typeof message.content === 'string' && message.content.trim())
      .map((message) => message.id);
    if (partialFeedbackMessageIds.length > 0) {
      setAbortedFeedbackMessageIds(current => new Set([...current, ...partialFeedbackMessageIds]));
    }

    setAborted(true);
    abortFuncs.current.forEach(a => a.abort('user aborted'));
    setShowLoadingMessage(false);
    setIsLoading(false);

    const prompt = dialogueMessages.findLast(message => message.role === 'user');
    if (prompt) {
      truncationInProgressRef.current = true;
      try {
        setAborted(await truncateConversationFrom(prompt.id));
      } finally {
        truncationInProgressRef.current = false;
      }
    }
  }

  /**
   * Handle pressing enter key to resubmit prompt
  **/
  const onEnterPress = (ev: React.KeyboardEvent<Element>, answer: ChatMessage) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      resubmitEditMessage(answer);
    }
  }

  /**
   * Handle resubmitting a prompt
  **/
  const resubmitEditMessage = async (answer: ChatMessage) => {
    if (!isLoading && !truncationInProgressRef.current) {
      if (answer.content !== editUserMessageContent && editUserMessageContent !== '') {
        const index = dialogueMessages.findIndex(m => m.id === answer.id);
        if (index < 0) return;
        setResubmitting(true);
        truncationInProgressRef.current = true;
        try {
          if (!await truncateConversationFrom(answer.id)) return;
          const retainedDialogueIds = new Set(dialogueMessages.slice(0, index).map(message => message.id));
          const nextMessages = messages.filter(message => message.role === STREAM_MESSAGE_ROLE.compaction
            ? !!message.anchorMessageId && retainedDialogueIds.has(message.anchorMessageId)
            : retainedDialogueIds.has(message.id));
          setMessages(nextMessages);
          truncationInProgressRef.current = false;
          void sendPrompt(editUserMessageContent, appStateContext?.state.currentChat?.id, false, nextMessages);
        } finally {
          truncationInProgressRef.current = false;
          setResubmitting(false);
        }
      }
      else setEditUserMessage('');
    }
  }

  /**
   * Handle canceling editing a message
  **/
  const cancelEditMessage = () => {
    setEditUserMessage('');
  }

  /**
   * Handle regenerating latest response by resubmitting user's last prompt
  **/
  const resubmitUserMessage = async () => {
    if (!isLoading && !resubmitting && !truncationInProgressRef.current) {
      const index = dialogueMessages.findLastIndex(message => message.role === 'user');
      const prompt = dialogueMessages[index];

      if (prompt) {
        setResubmitting(true);
        truncationInProgressRef.current = true;
        try {
          if (!await truncateConversationFrom(prompt.id)) return;
          const retainedDialogueIds = new Set(dialogueMessages.slice(0, index).map(message => message.id));
          const nextMessages = messages.filter(message => message.role === STREAM_MESSAGE_ROLE.compaction
            ? !!message.anchorMessageId && retainedDialogueIds.has(message.anchorMessageId)
            : retainedDialogueIds.has(message.id));
          setMessages(nextMessages);
          truncationInProgressRef.current = false;
          await sendPrompt(prompt.content, appStateContext?.state.currentChat?.id, false, nextMessages);
        } finally {
          truncationInProgressRef.current = false;
          setResubmitting(false);
        }
      }
    }
  }

  /**
   * Handle continueing latest response by submitting continue message
  **/
  const submitContinueMessage = async () => {
    if (!isLoading && !resubmitting) sendPrompt("*continue*", appStateContext?.state.currentChat?.id, true);
  }

  /** 
   * Handle copying the link to the Custom GPT
  **/
  const onCopyLink = () => {
    setCopyLinkText('Copied Link');
    navigator.clipboard.writeText(window.location.origin + "/spaces/v/" + appStateContext?.state.currentCustomGPT?.id);
    setTimeout(() => { setCopyLinkText('Copy Link'); }, 3000);
    handleTrackEvent('click', 'cgpt_copy_link', 'Custom GPT - Copy Link', '', appStateContext?.state.currentCustomGPT?.id || '', '');
  }
    
  /**
   * Handle removing Custom GPT from user's sidebar
  **/
  const onRemove = async () => {
    if (appStateContext?.state.currentCustomGPT) {
      // Add selected Custom GPT to current user
      await removeCustomGPTUser(appStateContext?.state.currentCustomGPT);

      // Refresh the user's Custom GPT list in sidebar
      handleRefreshUserCustomGPTs();

      handleTrackEvent('click', 'cgpt_remove_dd', 'Custom GPT - Remove (Dropdown)', '', appStateContext?.state.currentCustomGPT?.id || '', '');
    }
  }
  
  /** 
   * Handle navigating to open a Custom GPT for editing
  **/
  const onEdit = () => {
    if (appStateContext?.state.currentCustomGPT) navigate('/spaces/e/' + appStateContext?.state.currentCustomGPT.id);
    handleTrackEvent('click', 'cgpt_edit_dd', 'Custom GPT - Edit (Dropdown)', '', appStateContext?.state.currentCustomGPT?.id || '', '');
  }

  const scrollConversationToBottom = () => {
    window.requestAnimationFrame(() => {
      const scrollContainer = chatScrollContainer.current;
      if (scrollContainer) scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: "smooth" });
    });
  };

  const pollPowerpointStatus = async (statusUrl: string, presentationId: string) => {
    const maxAttempts = 60; // 5 minutes with 5 second intervals
    let attempts = 0;
    const pollChatId = activeChatIdRef.current;
    setLivePolledPresentationIds(ids => [...ids, presentationId]);

    const finish = (status: PresentationAsset["status"], error: string) => {
      setLivePolledPresentationIds(ids => ids.filter(id => id !== presentationId));
      setIsCreatingPowerpoint(false);
      setPresentations(current => current.map(asset => asset.id === presentationId
        ? { ...asset, status, error }
        : asset));
    };

    const poll = async () => {
      // The user moved on; the record is still updated server-side and will be
      // picked up by the status refresh when they come back.
      if (activeChatIdRef.current !== pollChatId) {
        setLivePolledPresentationIds(ids => ids.filter(id => id !== presentationId));
        return;
      }
      attempts++;

      const result = await handleCheckPowerpointStatus(statusUrl, presentationId);

      if (result.complete) {
        finish(result.success ? "ready" : "failed", result.success ? "" : "Presentation generation failed");
      } else if (attempts < maxAttempts) {
        // Continue polling
        setTimeout(poll, 5000); // Poll every 5 seconds
      } else {
        // Timeout
        finish("failed", "Presentation generation timed out");
      }
    };
    
    poll();
  }
      
  const onGenerateSlideStructure = async (changeRequest: string = "") => {
    if (!appStateContext?.state.currentChat?.id) return;
    scrollConversationToBottom();

    if (slideStructureData) {
      setIsRegeneratingSlides(true);
      scrollConversationToBottom();
    } else {
      setIsGeneratingSlideStructure(true);
      setCreateAiImages(false);
    }

    try {
      const response = await generateSlideStructure(
        appStateContext.state.currentChat.id,
        changeRequest,
        slideStructureData ?? null
      );

      if (response.ok) {
        const data = await response.json();
        const statusUrl = data.statusQueryGetUri;
        if (statusUrl) {
          handleTrackEvent('click', 'generate_slide_structure', 'Generate Slide Structure', '', appStateContext.state.currentChat.id, '');
          pollSlideStructureStatus(statusUrl);
        }
      } else {
        console.error('Failed to generate slide structure');
        setIsGeneratingSlideStructure(false);
        setIsRegeneratingSlides(false);
      }
    } catch (error) {
      console.error('Error generating slide structure:', error);
      setIsGeneratingSlideStructure(false);
      setIsRegeneratingSlides(false);
    }
  }

  const pollSlideStructureStatus = async (statusUrl: string) => {
    const maxAttempts = 48;
    let attempts = 0;
    const pollChatId = activeChatIdRef.current;

    const poll = async () => {
      // Dropping the result is correct here: a structure belongs to the
      // conversation it was generated from, and that one is no longer open.
      if (activeChatIdRef.current !== pollChatId) return;
      attempts++;
      const result = await handleCheckSlideStructureStatus(statusUrl);

      if (result.complete) {
        setIsGeneratingSlideStructure(false);
        setIsRegeneratingSlides(false);
        if (result.success && result.slideJson) {
          setSlideStructureData(result.slideJson);
          setTimeout(() => {
            chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        } else {
          console.error('Slide structure generation failed');
        }
      } else if (attempts < maxAttempts) {
        setTimeout(poll, 5000);
      } else {
        setIsGeneratingSlideStructure(false);
        setIsRegeneratingSlides(false);
        console.error('Slide structure generation timeout');
      }
    };

    poll();
  }

  const onConfirmSlideStructure = async (approvedJson: SlideStructure) => {
    if (!appStateContext?.state.currentChat?.id) return;
    const pendingPresentationId = `starting-${uuid()}`;
    const parentMessageId = [...messages].reverse().find((message) => message.role === "assistant")?.id || "";
    const pendingPresentation: PresentationAsset = {
      id: pendingPresentationId,
      conversationId: appStateContext.state.currentChat.id,
      parentMessageId,
      status: "starting",
      filename: getPresentationFilename(appStateContext.state.currentChat.title),
      blobName: "",
      createdAt: new Date().toISOString(),
    };
    setSlideStructureData(null);
    setIsCreatingPowerpoint(true);
    setPresentations((current) => [...current, pendingPresentation]);
    scrollConversationToBottom();

    try {
      const response = await createPowerpoint(
        appStateContext.state.currentChat.id,
        createAiImages,
        approvedJson
      );

      if (response.ok) {
        const data = await response.json();
        const presentation = data.presentation as PresentationAsset | undefined;
        if (presentation) setPresentations((current) => current.filter((asset) => asset.id !== presentation.id).map((asset) => asset.id === pendingPresentationId
          ? { ...presentation, parentMessageId: messages.some(message => message.id === presentation.parentMessageId) ? presentation.parentMessageId : parentMessageId, filename: getPresentationFilename(appStateContext.state.currentChat?.title, presentation.filename) }
          : asset));
        const statusUrl = data.statusQueryGetUri;
        if (statusUrl && presentation) {
          handleTrackEvent('click', 'confirm_slide_structure', 'Confirm Slide Structure', '', appStateContext.state.currentChat.id, '');
          pollPowerpointStatus(statusUrl, presentation.id);
        } else {
          setPresentations(current => current.map(asset => asset.id === (presentation?.id || pendingPresentationId)
            ? { ...asset, status: "failed", error: "Presentation generation could not be started" }
            : asset));
          setIsCreatingPowerpoint(false);
        }
      } else {
        setIsCreatingPowerpoint(false);
        setPresentations(current => current.map(asset => asset.id === pendingPresentationId
          ? { ...asset, status: "failed", error: "Presentation generation could not be started" }
          : asset));
      }
    } catch (error) {
      setIsCreatingPowerpoint(false);
      setPresentations(current => current.map(asset => asset.id === pendingPresentationId
        ? { ...asset, status: "failed", error: "Presentation generation could not be started" }
        : asset));
    }
  }

  /**
   * Handle getting SAS URL for selected file and opening it
   *
   * @param {string} filename - The selected filename
  **/
  const onOpenFile = async (filename: string) => {
    const fileName = (appStateContext?.state.currentChat?.id || '') + '/' + (filename || '');
    const blob_name = 'fileconv';
    const file_sas_url = await getSasUrl(fileName, blob_name);
    const sas_url = file_sas_url.blob_url;
    if (sas_url) window.open(sas_url, "_blank");
  }

  return (
    <div className={`content flex flex-col h-full ${appStateContext?.state.sidepanelOpen ? 'shifted-left' : ''} ${!isEmpty(appStateContext?.state.selectedCitations) ? 'shifted-right' : ''}`}>
      {isDragging ?
        <div className={`upload-screen ${appStateContext?.state.sidepanelOpen ? 'shifted-left' : ''} flex flex-col gap-4 items-center justify-center bg-neutral/85 backdrop-blur-sm text-white pointer-events-none`}>
          <div><FolderOpen size={60} /></div>
          <div className="text-3xl font-semibold">Add files</div>
          <div>Drop file here to add it to the conversation</div>
        </div>
      : null}
      <div className={`${screen.width <= 700 ? `flex justify-between` : `grid grid-cols-3`} items-center gap-2 ps-1 border-b ${!isEmpty(appStateContext?.state.currentChat) ? 'border-secondary' : 'border-transparent'}`}>
        <MessagePopup />

        <div className="flex items-center">
          {!appStateContext?.state.sidepanelOpen && !appStateContext?.state.sidepanelTransitioning &&
            <>
              <button type="button" className={`btn btn-ghost hover:bg-transparent dark:text-white px-2 mx-1`} onClick={handleToggleHistoryPanel}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-xl-heavy"><path fillRule="evenodd" clipRule="evenodd" d="M8.85719 3H15.1428C16.2266 2.99999 17.1007 2.99998 17.8086 3.05782C18.5375 3.11737 19.1777 3.24318 19.77 3.54497C20.7108 4.02433 21.4757 4.78924 21.955 5.73005C22.2568 6.32234 22.3826 6.96253 22.4422 7.69138C22.5 8.39925 22.5 9.27339 22.5 10.3572V13.6428C22.5 14.7266 22.5 15.6008 22.4422 16.3086C22.3826 17.0375 22.2568 17.6777 21.955 18.27C21.4757 19.2108 20.7108 19.9757 19.77 20.455C19.1777 20.7568 18.5375 20.8826 17.8086 20.9422C17.1008 21 16.2266 21 15.1428 21H8.85717C7.77339 21 6.89925 21 6.19138 20.9422C5.46253 20.8826 4.82234 20.7568 4.23005 20.455C3.28924 19.9757 2.52433 19.2108 2.04497 18.27C1.74318 17.6777 1.61737 17.0375 1.55782 16.3086C1.49998 15.6007 1.49999 14.7266 1.5 13.6428V10.3572C1.49999 9.27341 1.49998 8.39926 1.55782 7.69138C1.61737 6.96253 1.74318 6.32234 2.04497 5.73005C2.52433 4.78924 3.28924 4.02433 4.23005 3.54497C4.82234 3.24318 5.46253 3.11737 6.19138 3.05782C6.89926 2.99998 7.77341 2.99999 8.85719 3ZM6.35424 5.05118C5.74907 5.10062 5.40138 5.19279 5.13803 5.32698C4.57354 5.6146 4.1146 6.07354 3.82698 6.63803C3.69279 6.90138 3.60062 7.24907 3.55118 7.85424C3.50078 8.47108 3.5 9.26339 3.5 10.4V13.6C3.5 14.7366 3.50078 15.5289 3.55118 16.1458C3.60062 16.7509 3.69279 17.0986 3.82698 17.362C4.1146 17.9265 4.57354 18.3854 5.13803 18.673C5.40138 18.8072 5.74907 18.8994 6.35424 18.9488C6.97108 18.9992 7.76339 19 8.9 19H9.5V5H8.9C7.76339 5 6.97108 5.00078 6.35424 5.05118ZM11.5 5V19H15.1C16.2366 19 17.0289 18.9992 17.6458 18.9488C18.2509 18.8994 18.5986 18.8072 18.862 18.673C19.4265 18.3854 19.8854 17.9265 20.173 17.362C20.3072 17.0986 20.3994 16.7509 20.4488 16.1458C20.4992 15.5289 20.5 14.7366 20.5 13.6V10.4C20.5 9.26339 20.4992 8.47108 20.4488 7.85424C20.3994 7.24907 20.3072 6.90138 20.173 6.63803C19.8854 6.07354 19.4265 5.6146 18.862 5.32698C18.5986 5.19279 18.2509 5.10062 17.6458 5.05118C17.0289 5.00078 16.2366 5 15.1 5H11.5ZM5 8.5C5 7.94772 5.44772 7.5 6 7.5H7C7.55229 7.5 8 7.94772 8 8.5C8 9.05229 7.55229 9.5 7 9.5H6C5.44772 9.5 5 9.05229 5 8.5ZM5 12C5 11.4477 5.44772 11 6 11H7C7.55229 11 8 11.4477 8 12C8 12.5523 7.55229 13 7 13H6C5.44772 13 5 12.5523 5 12Z" fill="currentColor"></path></svg>
              </button>
              <button type="button" className={`btn btn-ghost hover:bg-transparent dark:text-white px-2 mx-1`} onClick={() => { navigate('/'); appStateContext?.dispatch({ type: 'SET_SEARCH_POPUP_OPEN', payload: true }); }}>
                <Search size={20} />
              </button>
              <button type="button" className={`btn btn-ghost hover:bg-transparent dark:text-white px-2 mx-1`} onClick={() => { navigate('/'); handleNewChat(); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="icon-xl-heavy"><path d="M15.6729 3.91287C16.8918 2.69392 18.8682 2.69392 20.0871 3.91287C21.3061 5.13182 21.3061 7.10813 20.0871 8.32708L14.1499 14.2643C13.3849 15.0293 12.3925 15.5255 11.3215 15.6785L9.14142 15.9899C8.82983 16.0344 8.51546 15.9297 8.29289 15.7071C8.07033 15.4845 7.96554 15.1701 8.01005 14.8586L8.32149 12.6785C8.47449 11.6075 8.97072 10.615 9.7357 9.85006L15.6729 3.91287ZM18.6729 5.32708C18.235 4.88918 17.525 4.88918 17.0871 5.32708L11.1499 11.2643C10.6909 11.7233 10.3932 12.3187 10.3014 12.9613L10.1785 13.8215L11.0386 13.6986C11.6812 13.6068 12.2767 13.3091 12.7357 12.8501L18.6729 6.91287C19.1108 6.47497 19.1108 5.76499 18.6729 5.32708ZM11 3.99929C11.0004 4.55157 10.5531 4.99963 10.0008 5.00007C9.00227 5.00084 8.29769 5.00827 7.74651 5.06064C7.20685 5.11191 6.88488 5.20117 6.63803 5.32695C6.07354 5.61457 5.6146 6.07351 5.32698 6.63799C5.19279 6.90135 5.10062 7.24904 5.05118 7.8542C5.00078 8.47105 5 9.26336 5 10.4V13.6C5 14.7366 5.00078 15.5289 5.05118 16.1457C5.10062 16.7509 5.19279 17.0986 5.32698 17.3619C5.6146 17.9264 6.07354 18.3854 6.63803 18.673C6.90138 18.8072 7.24907 18.8993 7.85424 18.9488C8.47108 18.9992 9.26339 19 10.4 19H13.6C14.7366 19 15.5289 18.9992 16.1458 18.9488C16.7509 18.8993 17.0986 18.8072 17.362 18.673C17.9265 18.3854 18.3854 17.9264 18.673 17.3619C18.7988 17.1151 18.8881 16.7931 18.9393 16.2535C18.9917 15.7023 18.9991 14.9977 18.9999 13.9992C19.0003 13.4469 19.4484 12.9995 20.0007 13C20.553 13.0004 21.0003 13.4485 20.9999 14.0007C20.9991 14.9789 20.9932 15.7808 20.9304 16.4426C20.8664 17.116 20.7385 17.7136 20.455 18.2699C19.9757 19.2107 19.2108 19.9756 18.27 20.455C17.6777 20.7568 17.0375 20.8826 16.3086 20.9421C15.6008 21 14.7266 21 13.6428 21H10.3572C9.27339 21 8.39925 21 7.69138 20.9421C6.96253 20.8826 6.32234 20.7568 5.73005 20.455C4.78924 19.9756 4.02433 19.2107 3.54497 18.2699C3.24318 17.6776 3.11737 17.0374 3.05782 16.3086C2.99998 15.6007 2.99999 14.7266 3 13.6428V10.3572C2.99999 9.27337 2.99998 8.39922 3.05782 7.69134C3.11737 6.96249 3.24318 6.3223 3.54497 5.73001C4.02433 4.7892 4.78924 4.0243 5.73005 3.54493C6.28633 3.26149 6.88399 3.13358 7.55735 3.06961C8.21919 3.00673 9.02103 3.00083 9.99922 3.00007C10.5515 2.99964 10.9996 3.447 11 3.99929Z" fill="currentColor"></path></svg>
              </button>
            </>
          }
          <button type="button" className="btn btn-ghost hover:bg-transparent cursor-default px-0 mx-0"></button>
          {(!shared && !isEmpty(appStateContext?.state.currentCustomGPT)) ? 
            <DropdownMenu>
              <DropdownMenuTrigger className={`model-dd rounded-lg ${screen.width <= 700 ? `` : `text-lg`} font-semibold select-none px-2 py-1 -mt-0.5 mx-1`}>
                <div className="flex items-center gap-2">
                  {!isEmpty(appStateContext?.state.currentCustomGPT?.image_path) ? <img src={`https://asappsmsstore.blob.core.windows.net/customgpticons/${appStateContext?.state.currentCustomGPT?.image_path}`} className="w-5 h-5 rounded-full object-cover" /> : <BrainCog size={18} />}
                  <span className="me-2">{appStateContext?.state.currentCustomGPT?.name || ''}</span>
                  <FontAwesomeIcon icon={faAngleDown} size="xs" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-secondary border-transparent rounded-3xl shadow-short p-3" style={{width:'175px'}} align="start" alignOffset={12}>
                <DropdownMenuItem className="dark:text-white text-md rounded-xl cursor-pointer focus:bg-white dark:focus:bg-neutral py-2 px-4" onClick={() => { navigate('/'); handleNewChat(); handleTrackEvent('click', 'new_chat_cgpt', 'Custom GPT - New Chat', '', appStateContext?.state.currentCustomGPT?.id || '', ''); }}>
                  <div><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="icon-xl-heavy"><path d="M15.6729 3.91287C16.8918 2.69392 18.8682 2.69392 20.0871 3.91287C21.3061 5.13182 21.3061 7.10813 20.0871 8.32708L14.1499 14.2643C13.3849 15.0293 12.3925 15.5255 11.3215 15.6785L9.14142 15.9899C8.82983 16.0344 8.51546 15.9297 8.29289 15.7071C8.07033 15.4845 7.96554 15.1701 8.01005 14.8586L8.32149 12.6785C8.47449 11.6075 8.97072 10.615 9.7357 9.85006L15.6729 3.91287ZM18.6729 5.32708C18.235 4.88918 17.525 4.88918 17.0871 5.32708L11.1499 11.2643C10.6909 11.7233 10.3932 12.3187 10.3014 12.9613L10.1785 13.8215L11.0386 13.6986C11.6812 13.6068 12.2767 13.3091 12.7357 12.8501L18.6729 6.91287C19.1108 6.47497 19.1108 5.76499 18.6729 5.32708ZM11 3.99929C11.0004 4.55157 10.5531 4.99963 10.0008 5.00007C9.00227 5.00084 8.29769 5.00827 7.74651 5.06064C7.20685 5.11191 6.88488 5.20117 6.63803 5.32695C6.07354 5.61457 5.6146 6.07351 5.32698 6.63799C5.19279 6.90135 5.10062 7.24904 5.05118 7.8542C5.00078 8.47105 5 9.26336 5 10.4V13.6C5 14.7366 5.00078 15.5289 5.05118 16.1457C5.10062 16.7509 5.19279 17.0986 5.32698 17.3619C5.6146 17.9264 6.07354 18.3854 6.63803 18.673C6.90138 18.8072 7.24907 18.8993 7.85424 18.9488C8.47108 18.9992 9.26339 19 10.4 19H13.6C14.7366 19 15.5289 18.9992 16.1458 18.9488C16.7509 18.8993 17.0986 18.8072 17.362 18.673C17.9265 18.3854 18.3854 17.9264 18.673 17.3619C18.7988 17.1151 18.8881 16.7931 18.9393 16.2535C18.9917 15.7023 18.9991 14.9977 18.9999 13.9992C19.0003 13.4469 19.4484 12.9995 20.0007 13C20.553 13.0004 21.0003 13.4485 20.9999 14.0007C20.9991 14.9789 20.9932 15.7808 20.9304 16.4426C20.8664 17.116 20.7385 17.7136 20.455 18.2699C19.9757 19.2107 19.2108 19.9756 18.27 20.455C17.6777 20.7568 17.0375 20.8826 16.3086 20.9421C15.6008 21 14.7266 21 13.6428 21H10.3572C9.27339 21 8.39925 21 7.69138 20.9421C6.96253 20.8826 6.32234 20.7568 5.73005 20.455C4.78924 19.9756 4.02433 19.2107 3.54497 18.2699C3.24318 17.6776 3.11737 17.0374 3.05782 16.3086C2.99998 15.6007 2.99999 14.7266 3 13.6428V10.3572C2.99999 9.27337 2.99998 8.39922 3.05782 7.69134C3.11737 6.96249 3.24318 6.3223 3.54497 5.73001C4.02433 4.7892 4.78924 4.0243 5.73005 3.54493C6.28633 3.26149 6.88399 3.13358 7.55735 3.06961C8.21919 3.00673 9.02103 3.00083 9.99922 3.00007C10.5515 2.99964 10.9996 3.447 11 3.99929Z" fill="currentColor"></path></svg></div>
                  <div className="text-sm text-base-500">New Chat</div>
                </DropdownMenuItem>
                <DropdownMenuItem className="dark:text-white text-md rounded-xl cursor-pointer focus:bg-white dark:focus:bg-neutral py-2 px-4 mt-2" onClick={() => { setIsRateDialogOpen(true); handleTrackEvent('click', 'cgpt_rate', 'Custom GPT - Rate', '', appStateContext?.state.currentCustomGPT?.id || '', ''); }}>
                  <div className="ps-[.0625rem]"><Star size={18} /></div>
                  <div className="text-sm text-base-500">Rate</div>
                </DropdownMenuItem>
                <DropdownMenuItem className="dark:text-white text-md rounded-xl cursor-pointer focus:bg-white dark:focus:bg-neutral py-2 px-4 mt-2" onClick={() => { setIsViewDialogOpen(true); handleTrackEvent('click', 'cgpt_about', 'Custom GPT - About', '', appStateContext?.state.currentCustomGPT?.id || '', ''); }}>
                  <div className="ps-[.0625rem]"><Info size={18} /></div>
                  <div className="text-sm text-base-500">About</div>
                </DropdownMenuItem>
                <DropdownMenuItem className="dark:text-white text-md rounded-xl cursor-pointer focus:bg-white dark:focus:bg-neutral py-2 px-4 mt-2" onClick={onCopyLink}>
                  <div className="ps-[.0625rem]"><Link2 size={18} /></div>
                  <div className="text-sm text-base-500">{copyLinkText}</div>
                </DropdownMenuItem>
                <DropdownMenuItem className="dark:text-white text-md rounded-xl cursor-pointer focus:bg-white dark:focus:bg-neutral py-2 px-4 mt-2" onClick={onRemove}>
                  <div className="ps-[.0625rem]"><PinOff size={18} /></div>
                  <div className="text-sm text-base-500">Remove</div>
                </DropdownMenuItem>
                {appStateContext?.state.currentCustomGPT?.created_by == appStateContext?.state.currentUser?.id ? 
                  <DropdownMenuItem className="dark:text-white text-md rounded-xl cursor-pointer focus:bg-white dark:focus:bg-neutral py-2 px-4 mt-2" onClick={onEdit}>
                    <div className="ps-[.0625rem]"><Pencil size={18} /></div>
                    <div className="text-sm text-base-500">Edit</div>
                  </DropdownMenuItem>
                : null}
              </DropdownMenuContent>
            </DropdownMenu>
          : <></> }
          {shared && currentChat && sharedConversationModelDetails ?
            <div className="flex flex-col items-start justify-center ms-2">
              {currentCustomGPT?.name ? <div className="truncate text-sm font-medium dark:text-white">{currentCustomGPT.name}</div> : null}
              <div className="truncate text-xs text-base-500 dark:text-white/75">{sharedConversationModelDetails}</div>
            </div>
          : null}
        </div>

        <div></div>

        <div className="flex justify-end items-center gap-2">
          {!isEmpty(appStateContext?.state.currentChat) ?
            <div
              className={`flex items-center gap-2 rounded-lg p-2 ${screen.width <= 700 ? `` : ` me-4`} ${(isCreatingPowerpoint || isGeneratingSlideStructure || presentationIsLast) ? 'opacity-50 cursor-not-allowed bg-neutral' : 'hover:bg-neutral cursor-pointer'}`}
              onClick={() => { if (!isCreatingPowerpoint && !isGeneratingSlideStructure && !presentationIsLast) onGenerateSlideStructure(""); }}
            >
              <Presentation size={16} className="mt-0.5 shrink-0" />
              {screen.width <= 700 ? null : <div className="select-none text-sm dark:text-white">{isGeneratingSlideStructure ? "Creating" : "Create"} PowerPoint</div>}
            </div>
          : null}

          {!isEmpty(appStateContext?.state.currentChat) ?
            <ConversationShareDialog
              conversation={appStateContext?.state.currentChat}
              trackEventItem="conv_share_top"
              trackEventText="Conversation - Share (Top)"
              trigger={
                <div className={`flex items-center gap-2 hover:bg-neutral rounded-lg cursor-pointer p-2 ${screen.width <= 700 ? `` : ` me-4`}`}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-label="" className="icon dark:text-white mt-0.5"><path d="M2.66821 12.6663V12.5003C2.66821 12.1331 2.96598 11.8353 3.33325 11.8353C3.70052 11.8353 3.99829 12.1331 3.99829 12.5003V12.6663C3.99829 13.3772 3.9992 13.8707 4.03052 14.2542C4.0612 14.6298 4.11803 14.8413 4.19849 14.9993L4.2688 15.1263C4.44511 15.4137 4.69813 15.6481 5.00024 15.8021L5.13013 15.8577C5.2739 15.9092 5.46341 15.947 5.74536 15.97C6.12888 16.0014 6.62221 16.0013 7.33325 16.0013H12.6663C13.3771 16.0013 13.8707 16.0014 14.2542 15.97C14.6295 15.9394 14.8413 15.8825 14.9993 15.8021L15.1262 15.7308C15.4136 15.5545 15.6481 15.3014 15.802 14.9993L15.8577 14.8695C15.9091 14.7257 15.9469 14.536 15.97 14.2542C16.0013 13.8707 16.0012 13.3772 16.0012 12.6663V12.5003C16.0012 12.1332 16.2991 11.8355 16.6663 11.8353C17.0335 11.8353 17.3313 12.1331 17.3313 12.5003V12.6663C17.3313 13.3553 17.3319 13.9124 17.2952 14.3626C17.2624 14.7636 17.1974 15.1247 17.053 15.4613L16.9866 15.6038C16.7211 16.1248 16.3172 16.5605 15.8215 16.8646L15.6038 16.9866C15.227 17.1786 14.8206 17.2578 14.3625 17.2952C13.9123 17.332 13.3553 17.3314 12.6663 17.3314H7.33325C6.64416 17.3314 6.0872 17.332 5.63696 17.2952C5.23642 17.2625 4.87552 17.1982 4.53931 17.054L4.39673 16.9866C3.87561 16.7211 3.43911 16.3174 3.13501 15.8216L3.01294 15.6038C2.82097 15.2271 2.74177 14.8206 2.70435 14.3626C2.66758 13.9124 2.66821 13.3553 2.66821 12.6663ZM9.33521 12.5003V4.9388L7.13696 7.13704C6.87732 7.39668 6.45625 7.39657 6.19653 7.13704C5.93684 6.87734 5.93684 6.45631 6.19653 6.19661L9.52954 2.86263L9.6311 2.77962C9.73949 2.70742 9.86809 2.66829 10.0002 2.66829C10.1763 2.66838 10.3454 2.73819 10.47 2.86263L13.804 6.19661C14.0633 6.45628 14.0634 6.87744 13.804 7.13704C13.5443 7.39674 13.1222 7.39674 12.8625 7.13704L10.6653 4.93977V12.5003C10.6651 12.8673 10.3673 13.1652 10.0002 13.1654C9.63308 13.1654 9.33538 12.8674 9.33521 12.5003Z"></path></svg>
                  {screen.width <= 700 ? null : <div className="select-none text-sm dark:text-white">Share</div>}
                </div>
              }
            />
          : null}
        </div>
      </div>

      <div className="flex flex-grow justify-center h-full">
        <div className="w-full mx-auto">
          <div className={`main-div flex flex-col flex-grow h-full items-center justify-center ${(appStateContext?.state.chatPanelTransitioned) ? 'transitioned' : ''}`}>
            {isLoadingConversation ?
              <div className="child-div flex flex-col w-full gg">
                <div className="child-div-parent flex flex-col flex-grow w-full items-center justify-center scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
                  <div className="loading-icon w-24 h-24"></div>
                </div>
              </div>
            :
              <div className="child-div flex flex-col w-full gg relative">
                {!appStateContext?.state.chatPanelTransitioned ?
                  <div className="flex items-center justify-center -mt-24">
                    {isEmpty(appStateContext?.state.currentCustomGPT) ? 
                      <div className="flex flex-col items-center text-center max-w-[1024px] w-full">
                        <div className="flex items-center">
                          <img src={home_logo} alt="ALLY" className="h-[200px] w-auto" />
                        </div>
                        {isWelcomeScreen ? <h1 className="greeting mt-2 mb-6 text-center text-lg sm:text-2xl dark:text-white">{welcomeGreeting}</h1> : null}
                        {(!isEmpty(appStateContext?.state.selectedTool) && appStateContext?.state.selectedTool == 'ms_office_data') ?
                          <div className="grid grid-cols-2 items-stretch w-full gap-4 my-6">
                            <div className="border border-accent rounded-lg cursor-pointer text-[13px] text-black/50 dark:text-white/75 px-4 py-2" onClick={() => { handleTrackEvent('click', 'ms_office_data_conv_starter1', 'Microsoft Data - Conv Starter 1', '', '', ''); sendPrompt('Briefly summarize all of my meetings for this week'); }}>
                              <pre className="whitespace-pre-wrap break-keep">Briefly summarize all of my meetings for this week</pre>
                            </div>
                            <div className="border border-accent rounded-lg cursor-pointer text-[13px] text-black/50 dark:text-white/75 px-4 py-2" onClick={() => { handleTrackEvent('click', 'ms_office_data_conv_starter2', 'Microsoft Data - Conv Starter 2', '', '', ''); sendPrompt('Briefly summarize all of my teams messages from yesterday'); }}>
                              <pre className="whitespace-pre-wrap break-keep">Briefly summarize all of my teams messages from yesterday</pre>
                            </div>
                            <div className="border border-accent rounded-lg cursor-pointer text-[13px] text-black/50 dark:text-white/75 px-4 py-2" onClick={() => { handleTrackEvent('click', 'ms_office_data_conv_starter3', 'Microsoft Data - Conv Starter 3', '', '', ''); sendPrompt('Briefly summarize all of my emails from yesterday'); }}>
                              <pre className="whitespace-pre-wrap break-keep">Briefly summarize all of my emails from yesterday</pre>
                            </div>
                            <div className="border border-accent rounded-lg cursor-pointer text-[13px] text-black/50 dark:text-white/75 px-4 py-2" onClick={() => { handleTrackEvent('click', 'ms_office_data_conv_starter4', 'Microsoft Data - Conv Starter 4', '', '', ''); sendPrompt('Briefly summarize all of my unread emails from last week'); }}>
                              <pre className="whitespace-pre-wrap break-keep">Briefly summarize all of my unread emails from last week</pre>
                            </div>
                          </div>
                        : null}
                      </div>
                    :
                      <div className="flex flex-col items-center text-center max-w-[1024px] w-full -mt-32 mb-6">
                        {!isEmpty(appStateContext?.state.currentCustomGPT?.image_path) ?
                          <img src={`https://asappsmsstore.blob.core.windows.net/customgpticons/${appStateContext?.state.currentCustomGPT?.image_path}`} className="w-24 h-24 rounded-full object-cover mb-2" alt={appStateContext?.state.currentCustomGPT?.name} />
                        : 
                          <div className="w-28 h-28"></div>
                        }
                        <h1 className="text-3xl font-bold dark:text-white mb-3">{appStateContext?.state.currentCustomGPT?.name}</h1>
                        {!isEmpty(appStateContext?.state.currentCustomGPT?.created_by_name) ? <h1 className="text-sm text-black/50 dark:text-white/75 mb-3">By: <strong>{appStateContext?.state.currentCustomGPT?.created_by_name}</strong></h1> : null}
                        <div className="text-sm text-black/50 dark:text-white/75">{(appStateContext?.state.currentCustomGPT?.description.length > 250 ? appStateContext?.state.currentCustomGPT?.description.substring(0, 250) + '...' : appStateContext?.state.currentCustomGPT?.description)}</div>
                        {(appStateContext?.state.currentCustomGPT?.conv_starter1 || appStateContext?.state.currentCustomGPT?.conv_starter2 || appStateContext?.state.currentCustomGPT?.conv_starter3 || appStateContext?.state.currentCustomGPT?.conv_starter4) ?
                          <div className="grid grid-cols-2 items-stretch w-full gap-4 mt-6">
                            {appStateContext?.state.currentCustomGPT?.conv_starter1 ?
                              <div className="border border-accent rounded-lg cursor-pointer text-[13px] text-black/50 dark:text-white/75 px-4 py-2" onClick={() => { handleTrackEvent('click', 'conv_starter1', 'Conversation Starter 1', '', appStateContext?.state.currentCustomGPT?.id || '', ''); sendPrompt(appStateContext?.state.currentCustomGPT?.conv_starter1 || ''); }}>
                                <pre className="whitespace-pre-wrap break-keep">{appStateContext?.state.currentCustomGPT?.conv_starter1}</pre>
                              </div>
                            : null}
                            {appStateContext?.state.currentCustomGPT?.conv_starter2 ?
                              <div className="border border-accent rounded-lg cursor-pointer text-[13px] text-black/50 dark:text-white/75 px-4 py-2" onClick={() => { handleTrackEvent('click', 'conv_starter2', 'Conversation Starter 2', '', appStateContext?.state.currentCustomGPT?.id || '', ''); sendPrompt(appStateContext?.state.currentCustomGPT?.conv_starter2 || ''); }}>
                                <pre className="whitespace-pre-wrap break-keep">{appStateContext?.state.currentCustomGPT?.conv_starter2}</pre>
                              </div>
                            : null}
                            {appStateContext?.state.currentCustomGPT?.conv_starter3 ?
                              <div className="border border-accent rounded-lg cursor-pointer text-[13px] text-black/50 dark:text-white/75 px-4 py-2" onClick={() => { handleTrackEvent('click', 'conv_starter3', 'Conversation Starter 3', '', appStateContext?.state.currentCustomGPT?.id || '', ''); sendPrompt(appStateContext?.state.currentCustomGPT?.conv_starter3 || ''); }}>
                                <pre className="whitespace-pre-wrap break-keep">{appStateContext?.state.currentCustomGPT?.conv_starter3}</pre>
                              </div>
                            : null}
                            {appStateContext?.state.currentCustomGPT?.conv_starter4 ?
                              <div className="border border-accent rounded-lg cursor-pointer text-[13px] text-black/50 dark:text-white/75 px-4 py-2" onClick={() => { handleTrackEvent('click', 'conv_starter4', 'Conversation Starter 4', '', appStateContext?.state.currentCustomGPT?.id || '', ''); sendPrompt(appStateContext?.state.currentCustomGPT?.conv_starter4 || ''); }}>
                                <pre className="whitespace-pre-wrap break-keep">{appStateContext?.state.currentCustomGPT?.conv_starter4}</pre>
                              </div>
                            : null}
                          </div>
                        : null}
                      </div>
                    }
                  </div>
                :
                  <div ref={chatScrollContainer} className="child-div-parent flex flex-col flex-grow w-full items-center scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
                    <div className="max-w-[1024px] w-full">
                      {timelineMessages.map((message, index) => message.role === STREAM_MESSAGE_ROLE.compaction ? (
                        <div key={message.id || `compaction-${index}`}><ContextCompactionDivider /></div>
                      ) : <div key={message.id}>
                        <Message
                          key={message.id}
                          messages={renderedMessages}
                          message={message}
                          index={renderedMessages.indexOf(message)}
                          processMessages={processMessages}
                          resubmitUserMessage={resubmitUserMessage}
                          convDisabled={readOnly}
                          editUserMessage={editUserMessage}
                          setEditUserMessage={setEditUserMessage}
                          setEditUserMessageContent={setEditUserMessageContent}
                          onEnterPress={onEnterPress}
                          resubmitting={resubmitting}
                          resubmitEditMessage={resubmitEditMessage}
                          cancelEditMessage={cancelEditMessage}
                          isSelecting={isSelecting}
                          isSummarizing={isSummarizing}
                          isQuerying={isQuerying}
                          submitContinueMessage={submitContinueMessage}
                          isLoading={isLoading}
                          feedbackDisabled={abortedFeedbackMessageIds.has(message.id)}
                          filesPending={!!pendingFilesMessageId && message.id === pendingFilesMessageId}
                        />
                        {presentations.filter(asset => asset.parentMessageId === message.id).map(asset => <PresentationAssetCard key={asset.id} asset={asset} onDownload={async (item) => { const url = await getPresentationDownloadUrl(item.id); if (url) void downloadUrl(url, item.filename); }} />)}
                      </div>)}
                      {presentations.filter(asset => !asset.parentMessageId).map(asset => <PresentationAssetCard key={asset.id} asset={asset} onDownload={async (item) => { const url = await getPresentationDownloadUrl(item.id); if (url) void downloadUrl(url, item.filename); }} />)}

                      {isGeneratingSlideStructure && !slideStructureData && (
                        <div className="px-1 my-6">
                          <LoadingState label="Generating slide structure" />
                        </div>
                      )}

                      {slideStructureData && (
                        <SlideStructurePreview
                          slideData={slideStructureData}
                          isRegenerating={isRegeneratingSlides}
                          createAiImages={createAiImages}
                          onSetCreateAiImages={setCreateAiImages}
                          onRegenerate={(req) => onGenerateSlideStructure(req)}
                          onConfirm={onConfirmSlideStructure}
                          onDismiss={() => setSlideStructureData(null)}
                        />
                      )}

                      {uploadProgress ? <div className="px-1"><UploadProgress progress={uploadProgress} /></div> : null}

                      {isSelecting ? <div className="pt-3 px-1"><LoadingState label="Selecting a Space" /></div> : null}
                      {compactionStatus ? <ContextCompactionDivider active /> : null}

                      {!indexing && !compactionStatus && !isSelecting && !isSummarizing && !isQuerying && showLoadingMessage && (
                        <Answer
                          answer={{
                            answer: 'Generating response...',
                            citations: []
                          }}
                          status={processMessages}
                          resubmitUserMessage={() => {}}
                          latest={false}
                          convDisabled={false}
                          terminateFileUpload={() => {}}
                        />
                      )}

                      {userMessageScrollSpacerHeight > 0 ? <div style={{ height: userMessageScrollSpacerHeight }} /> : null}
                      <div ref={chatMessageStreamEnd} />
                    </div>
                  </div>
                }
                <ChatScrollNav
                  messages={renderedMessages}
                  scrollContainerRef={chatScrollContainer}
                />
              </div>
            }
            <div className="max-w-screen-lg child-div flex flex-col shrink-0 w-full items-center justify-center py-1">
              <ConversationFilesPanel
                files={appStateContext?.state.individualFileChat || []}
                open={!!appStateContext?.state.convFilesOpen && isEmpty(appStateContext?.state.currentChat?.filename)}
                onOpenFile={onOpenFile}
                onClose={() => { appStateContext?.dispatch({ type: 'SET_CONV_FILES_OPEN', payload: false }); handleTrackEvent('click', 'conversation_files_hide', 'Conversation Files - Hide', '', '', ''); }}
              />
              {!readOnly ?
                <PromptInput
                  placeholder={currentCustomGPT ? "Chat with " + currentCustomGPT.name : (isImagePromptMode ? "Describe the image you want" : "Ask something")}
                  disabled={isLoading || resubmitting}
                  question={question}
                  setQuestion={setQuestion}
                  sendPrompt={(question, id) => sendPrompt(question, id)}
                  handleAbort={stopGenerating}
                  conversationId={appStateContext?.state.currentChat?.id || undefined}
                  imageSize={imageSize}
                  setImageSize={setImageSize}
                  imageStyle={imageStyle}
                  setImageStyle={setImageStyle}
                  marketingSearchLevel={marketingSearchLevel}
                  setMarketingSearchLevel={setMarketingSearchLevel}
                  longConversation={longConversation}
                  onOpenVoicePanel={() => setIsVoicePanelOpen(true)}
                  voiceModeEnabled={voiceModeEnabled}
                  isVoiceSessionActive={voiceSession.isActive}
                />
              : 
                (migrated ? <div className="flex bg-accent rounded text-xs py-1 px-4">This information has been migrated, please start a new conversation</div>
                : (shared ? <div></div>
                : <div className="flex bg-accent rounded text-xs py-1 px-4">You no longer have access to this Space, please start a new conversation</div>))
              }
            </div>
          </div>
        </div>
      </div>

      <VoiceModePanel
        open={isVoicePanelOpen}
        onOpenChange={setIsVoicePanelOpen}
        session={voiceSession.state}
        onStart={voiceSession.startSession}
        onStop={() => voiceSession.stopSession()}
        onToggleMute={voiceSession.toggleMute}
        voiceSpeed={voiceSession.voiceSpeed}
        onVoiceSpeedChange={voiceSession.setVoiceSpeed}
      />
      <SearchPopup />
      <RateDialog isRateDialogOpen={isRateDialogOpen} setIsRateDialogOpen={setIsRateDialogOpen} selectedCustomGPT={appStateContext?.state.currentCustomGPT || null} />
      <CustomGPTDetails isViewDialogOpen={isViewDialogOpen} setIsViewDialogOpen={setIsViewDialogOpen} selectedCustomGPT={appStateContext?.state.currentCustomGPT || null} editable={false} />
    </div>
  );
}

export default Chat;
